var DataTransformer        = require("./DataTransformer"),
    PageController         = require("app/core/PageController"),
    UserPaymentsRepository = require("app/Domain/Ott/Api/V2/DataAccess/UserPayments/Repository"),
    UpgradeRepository      = require("app/Domain/Ott/Api/V2/DataAccess/Upgrade/Repository"),
    PageView               = require("./PageView"),
    deepEqual              = require("fast-deep-equal"),
    get                    = require("object-path").get,
    kind                   = require("enyo/kind"),
    logger                 = require("enyo/logger"),
    quickMacroize          = require("enyo/macroize").quickMacroize;

module.exports = kind(/** @lends  app.ProfilePage.PageController.prototype */ {

    /**
     * @private
     */
    name: "app.ProfilePage.PageController",

    /**
     * @private
     */
    kind: PageController,


    /**
     * Implement the logic for loading and displaying the page here.
     *
     * @param  {Object}    activation           The activation object
     * @param  {Object}    activation.params    Parameters to tell us what to do
     * @param  {boolean}   activation.finished  Flag if activation is finished
     * @param  {function}  activation.resolve   A promise resolver to fire when
     *                                          the page is active.
     * @param  {function}  activation.reject    A promise rejector to fire when
     *                                          the page failed to activate.
     * @public
     */
    execute: function(activation) {
        var app             = this.app,
            $appView        = app.view,
            $router         = app.$.router,
            defaultLanguage = app.$.i18nService.getDefaultLanguage(),
            language        = get(activation, "params.language", null) || defaultLanguage,
            userPaymentsPromise,
            upgradePromise;

        // Loading graphic
        $appView.requestShowLoadingGraphic();

        // Load user payments
        userPaymentsPromise = UserPaymentsRepository.getPayments();

        // Load available upgrades
        upgradePromise      = UpgradeRepository.getUpgrades();

        // All data loaded, let's update them views
        Promise.all([userPaymentsPromise, upgradePromise])

            // Show View
            .then(this.bindSafely(function(responses) {
                var userPaymentsResponse = responses[0],
                    upgradeResponse      = responses[1],
                    userPaymentsData     = [],
                    canUpgrade           = false;

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                if (userPaymentsResponse.status === "ok") {
                    userPaymentsData = DataTransformer.transformData(language, userPaymentsResponse.data);
                }

                if (upgradeResponse.status === "ok") {
                    try {
                        canUpgrade = get(upgradeResponse, "data.offers", []).length > 0;
                    }
                    catch (error) {
                        canUpgrade = false;
                    }
                }

                this.updateState(activation.params, userPaymentsData, canUpgrade);
                this.updateView(PageView);
                this.updateDocumentTitle();
                activation.resolve(1);
            }))

            // Oops
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                activation.resolve(1, function() {
                    $router.redirect(quickMacroize("/{$language}/oops", {
                        language: language
                    }));
                });
            }))

            // No more loading graphic
            .finally(function() {
                $appView.requestHideLoadingGraphic();
            });
    },

    /**
     * @param  {Object}   params            The params passed to this controller
     * @param  {Array}    userPaymentsData  Freshly loaded user payments data
     * @param  {boolean}  canUpgrade        Whether the user can upgrade
     * @private
     */
    updateState: function(params, userPaymentsData, canUpgrade) {
        var state = {};

        // Build state
        state.params     = params;
        state.data       = {
            payments: userPaymentsData || []
        };
        state.canUpgrade = canUpgrade;

        // Update state only if it actually changed
        if (!deepEqual(this.state, state)) {
            this.set("state", state);
        }

        // Broadcast state changes to app state
        this.app.$.state.set("pageMediaType", null);
        this.app.$.state.set("pageMediaSlug", null);
        this.app.$.state.set("pageMediaId", null);
    },

    /**
     * Update the document title.
     *
     * @private
     */
    updateDocumentTitle: function() {
        var i18nService = this.app.$.i18nService;

        if (this.app.get("documentTitleOwner") !== this) {
            return;
        }

        this.app.setDocumentTitle(i18nService.getString("app.ProfilePage.Document.title"));
    }

});
